<template>
  <fragment>
    <mc-app-header title="Edit Notifications">
      <template #right>
        <div v-if="selectedEventKey" class="level-item">
          <b-button
            :loading="notificationSettings.submitting"
            type="is-primary"
            native-type="submit"
            form="notificationsForm"
            >Update</b-button
          >
        </div>
        <div v-if="eventKeys.length !== 0" class="level-item">
          <b-dropdown
            v-model="selectedEventKey"
            :disabled="notificationSettings.submitting"
            position="is-bottom-left"
          >
            <button
              slot="trigger"
              class="button"
              :class="{ 'is-primary': Boolean(selectedEventKey) }"
              type="button"
            >
              <template v-if="selectedEventKey">
                <span>{{ notifications[selectedEventKey].title }}</span>
              </template>
              <template v-else>
                <span>Select an Event</span>
              </template>
              <b-icon icon="menu-down"></b-icon>
            </button>

            <b-dropdown-item
              v-for="eventKey in eventKeys"
              :key="eventKey"
              @click="selectedEventKey = eventKey"
            >
              <div class="media">
                <div class="media-content">
                  <div class="title is-6">
                    {{ notifications[eventKey].title }}
                  </div>
                  <div class="content is-small">
                    {{ notifications[eventKey].description }}
                  </div>
                </div>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <div class="level-item">
          <b-button
            type="is-primary"
            icon-right="bell-plus"
            @click="addNewEventHandler"
          />
        </div>
      </template>
    </mc-app-header>
    <mc-app-body>
      <div class="edit-notification section">
        <b-loading
          v-if="notificationSettings.loadingSelected"
          active
          :is-full-page="false"
        />
        <mc-error-layout
          v-else-if="notificationSettings.selectError"
          message="Failed to load notifications!"
          :retry-fn="() => load()"
        />
        <mc-error-layout
          v-else-if="!selectedEventKey"
          message="Please add or select an event from above to edit!"
        />
        <div v-else>
          <div class="mc-container">
            <article class="media edit-notification__info">
              <div class="media-content">
                <div class="content">
                  <strong class="title is-5">{{
                    notifications[selectedEventKey].title
                  }}</strong>
                  <small>{{ selectedEventKey }}</small>
                  <br />
                  <p>{{ notifications[selectedEventKey].description }}</p>
                </div>
              </div>
              <div
                v-if="!notifications[selectedEventKey].persistent"
                class="media-right"
              >
                <b-button disabled class="is-danger" icon-right="delete" />
              </div>
            </article>
            <hr />
            <form
              id="notificationsForm"
              class="edit-notification__form"
              @submit.prevent="updateSettingsHandler"
            >
              <article
                v-for="(notification, index) in notifications[selectedEventKey]
                  .notifications"
                :key="index"
                class="media"
              >
                <div class="media-left">
                  <time-diff-input
                    v-if="notifications[selectedEventKey].hasMultiple"
                    v-model="notification.timeDiff"
                  />
                </div>
                <div class="media-content">
                  <div class="content">
                    <b-field label="Title">
                      <b-input
                        v-model="notification.title"
                        required
                        type="text"
                      ></b-input>
                    </b-field>
                    <b-field label="Description">
                      <b-input
                        v-model="notification.description"
                        required
                        type="textarea"
                      ></b-input>
                    </b-field>
                  </div>
                </div>
                <div class="media-right">
                  <b-button
                    v-if="
                      notifications[selectedEventKey].notifications.length > 1
                    "
                    class="edit-notification__delete is-danger"
                    icon-right="delete"
                    @click="deleteNotificationHandler(index)"
                  />
                </div>
              </article>
            </form>
            <hr />
            <b-button
              v-if="notifications[selectedEventKey].hasMultiple"
              class="is-primary"
              expanded
              @click="addNotificationHandler"
              >Add Notification</b-button
            >
          </div>
        </div>
      </div>
    </mc-app-body>
  </fragment>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { getNewNotificationEvent } from './NewNotificationEventModal';
import TimeDiffInput from './TimeDiffInput';

export default {
  data: function() {
    return {
      selectedEventKey: null
    };
  },
  computed: {
    ...mapState(['notificationSettings']),
    notifications() {
      return this.notificationSettings.selected;
    },
    eventKeys() {
      return Object.keys(this.notifications);
    }
  },
  methods: {
    ...mapActions('notificationSettings', [
      'load',
      'updateSettings',
      'addNewEvent',
      'addNotification',
      'deleteNotification'
    ]),
    validateAndSubmitNotificationsForm() {
      const form = this.$refs.notificationsForm.submit();
    },
    async addNewEventHandler() {
      try {
        const newEventData = await getNewNotificationEvent(this);
        await this.addNewEvent(newEventData);
        this.selectedEventKey = newEventData.slug;
      } catch (e) {
        this.$buefy.toast.open({
          message: `Adding new event ${
            e === 'escape' || e === 'x' || e == 'outside'
              ? 'canceled'
              : 'failed'
          }!`,
          type: 'is-danger'
        });
      }
    },
    addNotificationHandler() {
      try {
        this.addNotification(this.selectedEventKey);
      } catch (e) {
        this.$buefy.toast.open({
          message: 'Adding new notification failed',
          type: 'is-danger'
        });
      }
    },
    async deleteNotificationHandler(notificationIndex) {
      try {
        this.deleteNotification({
          eventKey: this.selectedEventKey,
          notificationIndex
        });
      } catch (e) {
        this.$buefy.toast.open({
          message: 'deleting notification failed',
          type: 'is-danger'
        });
      }
    },
    async updateSettingsHandler() {
      try {
        await this.updateSettings({
          ...this.notificationSettings.selected
        });
        this.$buefy.toast.open({
          message: 'Notifications updated successfully',
          type: 'is-success'
        });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to update Notifications!',
          type: 'is-danger'
        });
      }
    }
  },
  async created() {
    await this.load();
  },
  components: {
    TimeDiffInput
  }
};
</script>

<style lang="scss" scoped>
.edit-notification {
  &__info {
    small {
      margin: 0 0.4rem;
    }
  }
  &__form {
    margin-bottom: 2rem;
  }
  &__delete {
    margin-top: 1.5rem;
  }
}
</style>
